import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, h1, h2, table } from "@/lib/documentGenerator/docTheme";
import { SCENARIOS, GAPS, EVENTS, COVERAGE_SUMMARY, DOCUMENT_COVERAGE } from "@/lib/basProcess/scenarios";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export const generateScenarioAuditWordDocument = async (projectName?: string) => {
  const summary = COVERAGE_SUMMARY();
  const documentName = "Scenariogenomlysning av Bas-processen";

  const stamp = await createStampForCurrentUser({ documentName, projectName });

  const children: (Paragraph | Table)[] = [
    h1("1. Sammanfattning"),
    body(
      `Tio projektprofiler har bedömts ur åtta rollers perspektiv. Täckning: ${summary.percent} procent helt täckta perspektiv (${summary.klart} av ${summary.total}), ${summary.delvis} delvis täckta. ${summary.docsOk} av ${summary.docsTotal} dokument förekommer i minst två projekt.`
    ),
    h1("2. Projektprofiler"),
  ];

  SCENARIOS.forEach((s) => {
    children.push(
      h2(`${s.title} – ${s.size}`),
      body(s.summary),
      body(`Nyckelfaktorer: ${s.keyFactors.join(", ")}`, { muted: true }),
      body(`Skeden: ${s.phases.join(" → ")}`, { muted: true }),
      body(`Dokument: ${s.docs.map((d) => `Dok ${d}`).join(", ")}`, { muted: true }),
      table(
        ["Roll", "Täckning", "Fungerar idag", "Kvarstående lucka"],
        s.perspectives.map((p) => [p.role, p.coverage, p.works, p.gap]),
        { columnWidths: [1600, 1400, 3400, 2960] }
      )
    );
  });

  children.push(
    h1("3. Dokumenttäckning"),
    table(
      ["Dok", "Dokument", "Antal projekt", "Minst två"],
      DOCUMENT_COVERAGE().map((d) => [d.docNumber, d.title, String(d.count), d.ok ? "Ja" : "Nej"]),
      { columnWidths: [900, 5060, 1700, 1700] }
    ),
    h1("4. Händelsescenarier"),
    table(
      ["Projekt", "När och var", "Händelse", "Systemets svar", "Utfall"],
      EVENTS.map((e) => [
        SCENARIOS.find((s) => s.id === e.scenario)?.title ?? "",
        e.when,
        e.what,
        e.systemResponse,
        e.outcome,
      ]),
      { columnWidths: [1800, 1800, 2400, 2560, 800] }
    ),
    h1("5. Sammanställda luckor"),
    table(
      ["Lucka", "Prioritet", "Status", "Beskrivning"],
      GAPS.map((g) => [g.title, String(g.priority), g.status, g.description]),
      { columnWidths: [2400, 1200, 1400, 4360] }
    )
  );
  const footer = await documentFooter({ docNumber: "00", documentType: "scenario_audit", includeSignatures: false });

  const doc = buildDocument({
    meta: {
      version: footer.version,
      review: footer.review,
      documentName, projectName, status: `${summary.percent} % täckning` },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Scenariogenomlysning_${(projectName || "projekt").replace(/[^\w-]+/g, "_")}.docx`,
    {
      documentType: "scenario_audit",
      docNumber: "00",
      documentName,
      snapshot: { scenarios: SCENARIOS, gaps: GAPS },
      stamp,
    }
  );
};
